import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import './App.css';
import axios from 'axios';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from './design/components/Button';
import { Field } from './design/components/Field';
import StudentPortal from './student/StudentPortal';
import ClerkPortal from './clerk/ClerkPortal';
import AdminPortal from './admin/AdminPortal';
import SuperAdminPortal from './superadmin/SuperAdminPortal';
import EditProfileModal from './components/EditProfileModal';
import PreviewBanner from './components/PreviewBanner';
// Avatar component is imported inside each portal — nothing to do here.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Reusable Background Logo Component
const BackgroundLogo = () => (
  <div 
    className="fixed z-0 opacity-5 pointer-events-none"
    style={{
      top: '50%',
      left: '50%',
      width: '30vmin',
      height: '30vmin',
      transform: 'translate(-50%, -50%)',
      backgroundImage: 'url(https://customer-assets.emergentagent.com/job_implant-inventory/artifacts/g4gfj0jd_SAID%20logo.png)',
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
      filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.15))',
    }}
  />
);

// Auth Context
const AuthContext = createContext();

// ============================================================================
// In-app Confirm Dialog (replaces window.confirm/prompt which are blocked
// inside embedded preview iframes and on many mobile browsers).
// Usage:
//   const confirm = useConfirm();
//   const r = await confirm({ title, message, variant?, confirmLabel?,
//                             requireType?, requireReason? });
//   if (!r.confirmed) return;        // user cancelled
//   const reason = r.reason;          // populated when requireReason=true
// ============================================================================
const ConfirmContext = createContext(null);
const useConfirm = () => useContext(ConfirmContext);

const ConfirmProvider = ({ children }) => {
  const [opts, setOpts] = useState(null);
  const [typeText, setTypeText] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [busy, setBusy] = useState(false);
  const resolverRef = useRef(null);

  const confirm = (options) => {
    setOpts(options || {});
    setTypeText('');
    setReasonText('');
    setBusy(false);
    return new Promise((resolve) => { resolverRef.current = resolve; });
  };

  const close = (result) => {
    setOpts(null);
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  };

  const onCancel = () => close({ confirmed: false });
  const onConfirm = () => {
    if (opts.requireType && typeText !== opts.requireType) return;
    if (opts.requireReason && !reasonText.trim()) return;
    setBusy(true);
    close({ confirmed: true, reason: reasonText.trim() || null });
  };

  const variant = opts?.variant || 'danger';
  const variantStyles = {
    danger: { border: 'border-red-500', btn: 'bg-red-600 hover:bg-red-700', accent: 'text-red-700', emoji: '⚠️' },
    warning: { border: 'border-amber-500', btn: 'bg-amber-600 hover:bg-amber-700', accent: 'text-amber-700', emoji: '⚠️' },
    info: { border: 'border-blue-500', btn: 'bg-blue-600 hover:bg-blue-700', accent: 'text-blue-700', emoji: 'ℹ️' },
  }[variant] || { border: 'border-red-500', btn: 'bg-red-600 hover:bg-red-700', accent: 'text-red-700', emoji: '⚠️' };

  const canConfirm =
    !busy &&
    (!opts?.requireType || typeText === opts.requireType) &&
    (!opts?.requireReason || !!reasonText.trim());

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {opts && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[60]"
          data-testid="confirm-dialog"
          onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
        >
          <div className={`bg-white rounded-lg max-w-md w-full p-6 border-4 ${variantStyles.border}`}>
            <div className="flex items-start space-x-3 mb-4">
              <div className="text-3xl">{variantStyles.emoji}</div>
              <div className="flex-1">
                <h3 className={`text-xl font-bold ${variantStyles.accent}`} data-testid="confirm-title">
                  {opts.title || 'Confirm'}
                </h3>
                {opts.message && (
                  <p className="text-sm text-gray-700 mt-2 whitespace-pre-line" data-testid="confirm-message">
                    {opts.message}
                  </p>
                )}
              </div>
            </div>

            {opts.requireType && (
              <>
                <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-2 text-sm text-gray-800">
                  Type <code className="bg-gray-200 px-1 rounded font-mono font-bold">{opts.requireType}</code> to confirm:
                </div>
                <input
                  type="text"
                  value={typeText}
                  onChange={(e) => setTypeText(e.target.value)}
                  placeholder={opts.requireType}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-4 font-mono"
                  data-testid="confirm-type-input"
                  autoFocus
                />
              </>
            )}

            {opts.requireReason && (
              <textarea
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                placeholder={opts.reasonPlaceholder || 'Enter reason...'}
                rows={3}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
                data-testid="confirm-reason-input"
                autoFocus
              />
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={onCancel}
                disabled={busy}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                data-testid="confirm-cancel-btn"
              >
                {opts.cancelLabel || 'Cancel'}
              </button>
              <button
                onClick={onConfirm}
                disabled={!canConfirm}
                className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed font-semibold ${variantStyles.btn}`}
                data-testid="confirm-ok-btn"
              >
                {opts.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};
// ============================================================================

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      logout();
      return null;
    }
  };

  const refreshUser = () => fetchCurrentUser();

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, { username, password });
    const { access_token, user: userData, requires_password_change } = response.data;
    
    setToken(access_token);
    setUser(userData);
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    return { user: userData, requiresPasswordChange: requires_password_change };
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const changePassword = async (currentPassword, newPassword) => {
    const response = await axios.post(`${API}/auth/change-password`, {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  };

  const resetPassword = async (userId, newPassword = "said@1234") => {
    const response = await axios.post(`${API}/auth/reset-password`, {
      user_id: userId,
      new_password: newPassword
    });
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      changePassword, 
      resetPassword, 
      refreshUser,
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Login Component
const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(username, password);
    } catch (error) {
      setError(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="diis-scope diis-login-grid"
      data-testid="login-page"
    >
      {/* Left rail — editorial brand panel. No stock illustration. */}
      <aside className="diis-login-aside">
        {/* Fine geometric backdrop — implant-cross-section pattern.
            Deliberately extremely subtle so it functions as a background
            watermark rather than compete with the institutional text. */}
        <svg
          aria-hidden="true"
          className="diis-login-watermark"
          width="560"
          height="560"
          viewBox="0 0 560 560"
        >
          <defs>
            <pattern id="diis-grid" width="28" height="28" patternUnits="userSpaceOnUse">
              <path d="M 28 0 L 0 0 0 28" fill="none" stroke="var(--diis-hairline)" strokeWidth="0.4" />
            </pattern>
          </defs>
          <rect width="560" height="560" fill="url(#diis-grid)" />
          <circle cx="280" cy="280" r="240" stroke="var(--diis-hairline-strong)" strokeWidth="0.5" fill="none" />
          <circle cx="280" cy="280" r="170" stroke="var(--diis-accent)" strokeWidth="0.5" fill="none" opacity="0.35" />
          <circle cx="280" cy="280" r="100" stroke="var(--diis-accent)" strokeWidth="0.6" fill="none" opacity="0.5" />
          <circle cx="280" cy="280" r="40"  stroke="var(--diis-accent)" strokeWidth="0.6" fill="none" opacity="0.65" />
          <circle cx="280" cy="280" r="2.5" fill="var(--diis-accent)" opacity="0.75" />
          <line x1="280" y1="0" x2="280" y2="560" stroke="var(--diis-hairline-strong)" strokeWidth="0.35" />
          <line x1="0" y1="280" x2="560" y2="280" stroke="var(--diis-hairline-strong)" strokeWidth="0.35" />
        </svg>

        <header style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 16 }}>
          <img
            src="https://customer-assets.emergentagent.com/job_implant-inventory/artifacts/g4gfj0jd_SAID%20logo.png"
            alt="SAID — Saveetha Academy of Implantology & Dentistry"
            style={{ height: 64, width: 'auto', display: 'block' }}
            data-testid="said-logo-img"
          />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
            <span
              className="diis-display"
              style={{ fontSize: 20, letterSpacing: '-0.01em', color: 'var(--diis-ink)' }}
            >
              SAID <span style={{
                fontFamily: 'var(--diis-font-sans)',
                fontWeight: 500,
                fontSize: 11,
                letterSpacing: '0.24em',
                marginLeft: 4,
                color: 'var(--diis-ink-muted)',
                verticalAlign: '3px',
              }}>— DIIS</span>
            </span>
            <span
              style={{
                marginTop: 4,
                fontSize: 11,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: 'var(--diis-ink-subtle)',
                fontWeight: 500,
              }}
            >
              Dental Implant Inventory System
            </span>
          </div>
        </header>

        <div style={{ position: 'relative', zIndex: 1, maxWidth: 520 }}>
          <h2
            className="diis-display diis-login-h2"
            style={{
              lineHeight: 1.18,
              margin: 0,
              fontWeight: 500,
              letterSpacing: '-0.018em',
              color: 'var(--diis-ink)',
              textTransform: 'uppercase',
            }}
          >
            Digital Inventory Management
            <br />
            <span style={{ color: 'var(--diis-accent-ink)' }}>for Implantology</span>
          </h2>

          <hr className="diis-hairline" style={{ margin: '28px 0 20px', maxWidth: 80, borderTopWidth: 1.5, borderColor: 'var(--diis-hairline-strong)' }} />

          <p
            style={{
              margin: 0,
              fontFamily: 'var(--diis-font-display)',
              fontSize: 19,
              fontWeight: 500,
              letterSpacing: '-0.005em',
              lineHeight: 1.45,
              color: 'var(--diis-ink)',
            }}
          >
            Department of Implantology
          </p>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 15,
              lineHeight: 1.5,
              color: 'var(--diis-ink-muted)',
              fontWeight: 500,
            }}
          >
            Saveetha Dental College &amp; Hospitals
          </p>
          <p
            style={{
              margin: '10px 0 0',
              fontSize: 12,
              lineHeight: 1.5,
              letterSpacing: '0.02em',
              color: 'var(--diis-ink-subtle)',
              fontWeight: 400,
            }}
          >
            Saveetha Institute of Medical and Technical Sciences
          </p>
        </div>

        <footer style={{ position: 'relative', zIndex: 1 }}>
          <hr className="diis-hairline" style={{ marginBottom: 16 }} />
          <div className="diis-eyebrow" style={{ color: 'var(--diis-ink-subtle)' }}>
            An Institutional Clinical Platform
          </div>
        </footer>
      </aside>

      {/* Right — the actual sign-in form. */}
      <main className="diis-login-main">
        <div className="diis-login-form-wrap">
          <div className="diis-eyebrow" style={{ marginBottom: 12 }}>
            Sign in to continue
          </div>
          <h1
            className="diis-display diis-login-h1"
            style={{ margin: 0, letterSpacing: '-0.02em' }}
          >
            Welcome back.
          </h1>
          <p
            style={{
              marginTop: 8,
              marginBottom: 40,
              color: 'var(--diis-ink-muted)',
              fontSize: 14,
            }}
          >
            Use your institution-issued credentials.
          </p>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {error && (
              <div
                role="alert"
                data-testid="login-error"
                style={{
                  padding: '12px 14px',
                  border: '1px solid var(--diis-danger)',
                  background: 'var(--diis-danger-soft)',
                  color: 'var(--diis-danger)',
                  borderRadius: 'var(--diis-radius)',
                  fontSize: 13,
                  lineHeight: 1.45,
                }}
              >
                {error}
              </div>
            )}

            <Field
              id="username"
              label="Username"
              placeholder="e.g. samjebaraj"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              data-testid="login-username-input"
            />

            <Field
              id="password"
              label="Password"
              type="password"
              placeholder="••••••••"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password-input"
            />

            <Button
              type="submit"
              variant="accent"
              size="lg"
              fullWidth
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          <p
            style={{
              marginTop: 40,
              fontSize: 12,
              color: 'var(--diis-ink-subtle)',
              lineHeight: 1.7,
            }}
          >
            Forgot your password? Please contact a SuperAdmin — passwords are
            reset internally to preserve the departmental audit trail.
          </p>
        </div>
      </main>
    </div>
  );
};

const PasswordChangeModal = ({ showModal, setShowModal, isFirstTime = false, onSuccess }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { changePassword } = useAuth();

  useEffect(() => {
    if (showModal) {
      setError('');
      setSuccess('');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);
    }
  }, [showModal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      await changePassword(currentPassword, newPassword);
      setSuccess('Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      if (onSuccess) onSuccess();
      window.setTimeout(() => setShowModal(false), 900);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error changing password');
    } finally {
      setLoading(false);
    }
  };

  const renderPwdInput = (id, value, setValue, visible, setVisible, label, testid) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="form-input w-full"
          style={{ paddingRight: 40 }}
          data-testid={testid}
          required
          autoComplete="new-password"
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          data-testid={`${testid}-toggle`}
          tabIndex={-1}
          style={{
            position: 'absolute',
            top: '50%',
            right: 10,
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: '#6B7280',
            padding: 4,
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md" data-testid="change-password-modal">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {isFirstTime ? 'Change Default Password' : 'Change Password'}
        </h3>

        {isFirstTime && (
          <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mb-4">
            <p className="text-sm text-amber-800">
              <strong>Security Notice:</strong> You are using the default password. Please change it for security.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm"
              data-testid="change-password-error"
            >
              {error}
            </div>
          )}
          {success && (
            <div
              className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm"
              data-testid="change-password-success"
            >
              {success}
            </div>
          )}

          {renderPwdInput(
            'cp-current', currentPassword, setCurrentPassword,
            showCurrent, setShowCurrent,
            'Current Password', 'cp-current-input'
          )}
          {renderPwdInput(
            'cp-new', newPassword, setNewPassword,
            showNew, setShowNew,
            'New Password', 'cp-new-input'
          )}
          <p className="text-xs text-gray-500 -mt-2">Minimum 6 characters</p>
          {renderPwdInput(
            'cp-confirm', confirmPassword, setConfirmPassword,
            showConfirm, setShowConfirm,
            'Confirm New Password', 'cp-confirm-input'
          )}

          <div className="flex justify-end space-x-3 pt-2">
            {!isFirstTime && (
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="btn-secondary"
                disabled={loading}
                data-testid="cp-cancel-btn"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
              data-testid="cp-submit-btn"
            >
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =============================================================================
// Approval History view — used by both Admin (sees only own) and SuperAdmin
// (sees ALL faculty approvals with a faculty filter dropdown).
// =============================================================================
const App = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [previewRole, setPreviewRole] = useState(null); // 'admin' | 'clerk' | 'student' | null — SuperAdmin visual-preview
  const { isAuthenticated, user, login, logout, refreshUser } = useAuth();

  // Set default tab based on user role
  useEffect(() => {
    if (user) {
      if (user.role === 'admin' || user.role === 'superadmin') {
        setActiveTab('dashboard');
      } else if (user.role === 'student') {
        setActiveTab('orders'); // Students should see their request management interface
      } else if (user.role === 'clerk') {
        setActiveTab('returns'); // Clerk lands on ClerkDashboard (issuance + returns)
      } else {
        setActiveTab('inventory');
      }
      
      // Check if password change is required
      if (user.must_change_password || user.is_first_login) {
        setRequiresPasswordChange(true);
        setShowPasswordModal(true);
      }
    }
  }, [user]);

  if (!isAuthenticated) {
    return <Login />;
  }

  // Students get the redesigned Student Portal — completely separate shell
  // from the admin/superadmin/clerk workflow. It reuses the same auth token
  // via /api/client.js and every existing backend endpoint. No schema,
  // no backend, no data has been changed.
  if (user?.role === 'student') {
    return (
      <>
        <StudentPortal
          user={user}
          onChangePassword={() => setShowPasswordModal(true)}
          onEditProfile={() => setShowEditProfileModal(true)}
          onLogout={logout}
        />
        <PasswordChangeModal
          showModal={showPasswordModal}
          setShowModal={setShowPasswordModal}
          isFirstTime={requiresPasswordChange}
          onSuccess={() => setRequiresPasswordChange(false)}
        />
        <EditProfileModal
          open={showEditProfileModal}
          onClose={() => setShowEditProfileModal(false)}
          user={user}
          onRefreshUser={refreshUser}
          onOpenChangePassword={() => setShowPasswordModal(true)}
        />
      </>
    );
  }

  // Admin (Faculty) — Redesigned Faculty Console owns every admin surface
  // now (Overview, Requests, Overdue, History, Inventory, Returns, Waitlist,
  // Users). SuperAdmin lands on its own redesigned SuperAdmin Portal
  // (Overview, Users, Reassign, Activities). Both are fully self-contained.
  // SuperAdmin visual-preview — render the chosen role's portal with the
  // SuperAdmin's own user object (role shimmed) + a banner on top. No auth
  // swap; API calls still carry the SuperAdmin JWT.
  if (user?.role === 'superadmin' && previewRole) {
    const shimmedUser = {
      ...user,
      role: previewRole,
      // Student portal needs a category to render the correct dashboard —
      // fall back to a benign default when previewing.
      student_category: previewRole === 'student' ? (user.student_category || 'Fellow Implant') : user.student_category,
    };
    const exit = () => setPreviewRole(null);
    const PortalCmp =
      previewRole === 'admin'   ? AdminPortal   :
      previewRole === 'clerk'   ? ClerkPortal   :
      previewRole === 'student' ? StudentPortal : null;
    // Unreachable in practice — the dropdown only emits valid roles —
    // but fall back to a clean exit rather than call a state setter in render.
    if (!PortalCmp) return null;
    return (
      <>
        <PreviewBanner role={previewRole} onExit={exit} />
        <PortalCmp
          user={shimmedUser}
          onChangePassword={exit}
          onEditProfile={exit}
          onLogout={exit}
        />
      </>
    );
  }

  if (user?.role === 'superadmin') {
    return (
      <>
        <SuperAdminPortal
          user={user}
          onChangePassword={() => setShowPasswordModal(true)}
          onEditProfile={() => setShowEditProfileModal(true)}
          onLogout={logout}
          onPreviewRole={setPreviewRole}
        />
        <PasswordChangeModal
          showModal={showPasswordModal}
          setShowModal={setShowPasswordModal}
          isFirstTime={requiresPasswordChange}
          onSuccess={() => setRequiresPasswordChange(false)}
        />
        <EditProfileModal
          open={showEditProfileModal}
          onClose={() => setShowEditProfileModal(false)}
          user={user}
          onRefreshUser={refreshUser}
          onOpenChangePassword={() => setShowPasswordModal(true)}
        />
      </>
    );
  }

  if (user?.role === 'admin') {
    return (
      <>
        <AdminPortal
          user={user}
          onChangePassword={() => setShowPasswordModal(true)}
          onEditProfile={() => setShowEditProfileModal(true)}
          onLogout={logout}
        />
        <PasswordChangeModal
          showModal={showPasswordModal}
          setShowModal={setShowPasswordModal}
          isFirstTime={requiresPasswordChange}
          onSuccess={() => setRequiresPasswordChange(false)}
        />
        <EditProfileModal
          open={showEditProfileModal}
          onClose={() => setShowEditProfileModal(false)}
          user={user}
          onRefreshUser={refreshUser}
          onOpenChangePassword={() => setShowPasswordModal(true)}
        />
      </>
    );
  }

  // Record Clerks get the redesigned Clerk Portal — same isolation pattern
  // as the Student Portal. Reuses every existing backend endpoint; no
  // schema, no backend, no data changed.
  if (user?.role === 'clerk') {
    return (
      <>
        <ClerkPortal
          user={user}
          onChangePassword={() => setShowPasswordModal(true)}
          onEditProfile={() => setShowEditProfileModal(true)}
          onLogout={logout}
        />
        <PasswordChangeModal
          showModal={showPasswordModal}
          setShowModal={setShowPasswordModal}
          isFirstTime={requiresPasswordChange}
          onSuccess={() => setRequiresPasswordChange(false)}
        />
        <EditProfileModal
          open={showEditProfileModal}
          onClose={() => setShowEditProfileModal(false)}
          user={user}
          onRefreshUser={refreshUser}
          onOpenChangePassword={() => setShowPasswordModal(true)}
        />
      </>
    );
  }

  // Safety fallback — every real role above is covered.
  return <Login />;
};

const AppWithAuth = () => {
  return (
    <AuthProvider>
      <ConfirmProvider>
        <App />
      </ConfirmProvider>
    </AuthProvider>
  );
};

export default AppWithAuth;